import { Template } from "../../VideoEditor";
import { FieldContainer } from '../CaptionConfig/FieldContainer';
import { ColorComponent, DEFAULT_COLOR_SETS } from '../ColorConfig/ColorConfig';
import { MediaItem } from "../../../../common/MediaPicker/MediaPicker";
import { useState, useEffect, useRef } from "react";
import { MediaSelector } from '../../../../common/MediaPicker/MediaSelector';
import { IconDropdown } from '../LayoutConfig/IconDropdown';
import { SquareBorderIcon, SmoothBorderIcon, RoundedBorderIcon } from './../FrameConfig/BorderRadiusIcons';
import { NoBorderIcon, ShadowIcon, DarkBorderIcon, LightBorderIcon, Border3DLeftIcon, Border3DRightIcon } from './../FrameConfig/BorderStyleIcons';
import { FontSelect, FontValue } from '../CaptionConfig/FontSelect';
import { ThemeSystemConfig } from "../../../remotion/Components/Theme/Theme";
import { CaptionDesignSystemConfig, SavedCaptionDesignSystemConfig } from "../../../remotion/Components/Captions/Caption";
import { ClassicCaptionPreview } from "../../../remotion/Components/Captions/ClassicCaptions/ClassicCaptionPreview";
import { CaptionPage } from "../../../remotion/Components/Captions/CaptionGrouper";
import { Position } from "../../../remotion/Components/DesignSystem";
import { ArrowLeftOutlined } from '@ant-design/icons';
import TrebbleClientAPIHelper from "../../../../../models/helper/TrebbleClientAPI";
import FileUploadHelper from "../../../../../models/helper/FileUploadHelper";
import { BrandSystemConfig } from "../../../remotion/Components/Theme/Brand";
import { CaptionList } from "../CaptionConfig/CaptionList";
import TrebbleSelect from "../../../../common/TrebbleSelect";
import TrebbleColorPicker from "../../../../common/TrebbleColorPicker";
import TrebbleCard from "../../../../common/TrebbleCard";
import TrebbleButton from "../../../../common/TrebbleButton";
import TrebbleRadio, { TrebbleRadioChangeEvent } from "../../../../common/TrebbleRadio";
import TrebbleInput from "../../../../common/TrebbleInput";
import TrebbleMessage from "../../../../common/TrebbleMessage";
import ti18n from 'i18n!nls/ClipCreatori18n';
import Utils from "../../../../../../js/models/helper/Utils";

// Extend Window interface if not already done globally
declare global {
    interface Window {
      getI18n: (i18nData: any, key: string, options?: any) => string;
    }
  }

interface Theme {
    id: string;
    name: string;
    type: 'user' | 'trebble';
    themeConfig: ThemeSystemConfig;
}

export interface Caption {
    id: string;
    name: string;
    type: 'user' | 'trebble';
    captionConfig: CaptionDesignSystemConfig;
}

interface Category {
    id: string;
    name: string;
    captions: Caption[];
}

interface BackgroundImageAsset {
    assetId: string;
    name: string;
    ownerUserUID: string;
    url: string;
    type: string;
    visibility: string;
    creationDate: string;
}

// Add interface for the API response caption format
interface ApiCaption {
    id: string;
    name: string;
    category?: string;
    type: 'user' | 'trebble';
    captionConfig: {
        milisecondGrouping: number;
        font: string;
        useDesignSystemColor: boolean;
        mainColor: string;
        highlightColor: string;
        fontSizeRatio: "small" | "medium" | "large";
        outline: "none" | "glow" | "shadow" | "3d-shadow" | "stroke" | "3d-stroke";
        background: "none" | "white" | "colored" | "translucent";
        textAnimation: "none" | "pop" | "highlight" | "boxed" | "scale" | "karaoke" | "reveal" | "reveal_up" | "reveal_scale" | "reveal_box" | "karaoke_scale" | "karaoke_box";
        borderRadius: number;
        captionCase: "standard" | "uppercase";
        hidePunctuation: boolean;
        position: "top left" | "top center" | "top right" | "bottom left" | "bottom center" | "bottom right";
        maxLines: "one_line" | "two_lines" | "three_lines";
        showOneWordOnly: boolean;
    };
}

const BORDER_STYLES = Object.freeze({
  NO_BORDER: {
      value: "no_border",
      label: window.getI18n(ti18n, "BORDER_STYLE_NONE"),
      icon: <NoBorderIcon />
  },
  SHADOW: {
      value: "shadow",
      label: window.getI18n(ti18n, "BORDER_STYLE_SHADOW"),
      icon: <ShadowIcon />
  },
  DARK_BORDER: {
      value: "dark_border",
      label: window.getI18n(ti18n, "BORDER_STYLE_DARK"),
      icon: <DarkBorderIcon />
  },
  LIGHT_BORDER: {
      value: "light_border",
      label: window.getI18n(ti18n, "BORDER_STYLE_LIGHT"),
      icon: <LightBorderIcon />
  },
  BORDER_WITH_3D_LEFT: {
      value: "border_with_3d_left",
      label: window.getI18n(ti18n, "BORDER_STYLE_3D_LEFT"),
      icon: <Border3DLeftIcon />
  },
  BORDER_WITH_3D_RIGHT: {
      value: "border_with_3d_right",
      label: window.getI18n(ti18n, "BORDER_STYLE_3D_RIGHT"),
      icon: <Border3DRightIcon />
  },
});

const BORDER_CORNER_RADIUS = Object.freeze({
  SQUARE: {
      value: 0,
      label: window.getI18n(ti18n, "BORDER_RADIUS_SQUARE"),
      icon: <SquareBorderIcon />
  },
  SMOOTH: {
      value: 0.10,
      label: window.getI18n(ti18n, "BORDER_RADIUS_SMOOTH"),
      icon: <SmoothBorderIcon />
  },
  ROUNDED: {
      value: 0.30,
      label: window.getI18n(ti18n, "BORDER_RADIUS_ROUNDED"),
      icon: <RoundedBorderIcon />
  },
});

const BACKGROUND_STYLES = Object.freeze({
  COLOR: {
      value: "color",
      label: window.getI18n(ti18n, "BACKGROUND_STYLE_COLOR"),
  },
  IMAGE: {
      value: "image",
      label: window.getI18n(ti18n, "BACKGROUND_STYLE_IMAGE"),
  },
});

const BACKGROUND_CATEGORIES = [
  { value: 'nature', label: window.getI18n(ti18n, "BG_CATEGORY_NATURE") },
  { value: 'abstract', label: window.getI18n(ti18n, "BG_CATEGORY_ABSTRACT") },
  { value: 'patterns', label: window.getI18n(ti18n, "BG_CATEGORY_PATTERNS") },
];

// Demo media items - replace with your actual media items
const BACKGROUND_MEDIA_ITEMS: MediaItem[] = [
  {
      id: '1',
      url: 'https://picsum.photos/id/10/1920/1080',
      type: 'image',
      title: 'Nature Background 1',
      category: 'nature',
  },
  {
      id: '2',
      url: 'https://picsum.photos/id/20/1920/1080',
      type: 'image',
      title: 'Abstract Pattern',
      category: 'abstract',
  },
  // Add more items as needed
];

const getColorSetLabelFromValues = (primaryColor: string, secondaryColor: string, accentColor: string) => {  
  const colorSet = Object.values(DEFAULT_COLOR_SETS).find(
    (colorSet) =>
      colorSet.primaryColor.toLowerCase() === primaryColor.toLowerCase() &&
      colorSet.secondaryColor.toLowerCase() === secondaryColor.toLowerCase() &&
      colorSet.accentColor.toLowerCase() === accentColor.toLowerCase()
  );
  return colorSet?.value || window.getI18n(ti18n, "CUSTOM_COLOR_SET");
}

interface ThemeEditFormProps {
    theme: Theme;
    onThemeChange: (theme: Theme) => void;
    onPickerVisibilityChange?: (isVisible: boolean) => void;
    workspaceId: string | null | undefined;
}

const FONT_OUTLINE = Object.freeze({
    NONE: {
        value: "none",
        label: window.getI18n(ti18n, "STYLE_NONE"),
    },
    SHADOW: {
        value: "shadow",
        label: window.getI18n(ti18n, "FONT_OUTLINE_SHADOW"),
    },
    GLOW: {
        value: "glow",
        label: window.getI18n(ti18n, "FONT_OUTLINE_GLOW"),
    },
    STROKE: {
        value: "stroke",
        label: window.getI18n(ti18n, "FONT_OUTLINE_STROKE"),
    },
    "3D_SHADOW": {
        value: "3d-shadow",
        label: window.getI18n(ti18n, "FONT_OUTLINE_3D_SHADOW"),
    },
    "3D_STROKE":{
        value: "3d-stroke",
        label: window.getI18n(ti18n, "FONT_OUTLINE_3D_STROKE")
    }
});

// Replace simulated API call with actual API call
const fetchUserCaptions = async (workspaceId: string | null | undefined): Promise<SavedCaptionDesignSystemConfig[]> => {
    const api = TrebbleClientAPIHelper.getInstance();
    const captions = await api.getCaptions({ type: 'user' }, workspaceId);
    return captions.map((caption: ApiCaption) => ({
        ...caption,
        type: 'user' as const
    }));
};

const fetchTrebbleCaptions = async (workspaceId: string | null | undefined): Promise<SavedCaptionDesignSystemConfig[]> => {
    const api = TrebbleClientAPIHelper.getInstance();
    const captions = await api.getCaptions({ type: 'trebble' }, workspaceId);
    return captions.map((caption: ApiCaption) => ({
        ...caption,
        type: 'trebble' as const
    }));
};

export const ThemeEditForm = ({ theme, onThemeChange, onPickerVisibilityChange, workspaceId }: ThemeEditFormProps) => {
  const [isBackgroundPickerVisible, setIsBackgroundPickerVisible] = useState(false);
  const [isCaptionPickerVisible, setIsCaptionPickerVisible] = useState(false);
  const [userCaptions, setUserCaptions] = useState<SavedCaptionDesignSystemConfig[]>([]);
  const [trebbleCaptionsByCategory, setTrebbleCaptionsByCategory] = useState<{ [key: string]: SavedCaptionDesignSystemConfig[] }>({});
  const [backgroundImages, setBackgroundImages] = useState<MediaItem[]>([]);
  // Tracks which workspaceId the cached `backgroundImages` were fetched for.
  // Without this we keyed cache validity off `backgroundImages.length === 0`,
  // so once images loaded for any workspace, switching workspaces would never
  // refetch — the picker kept showing the previous workspace's images.
  const [backgroundImagesWorkspaceId, setBackgroundImagesWorkspaceId] = useState<string | null | undefined>(undefined);
  const [isLoadingBackgroundImages, setIsLoadingBackgroundImages] = useState(false);
  const [backgroundImageError, setBackgroundImageError] = useState<string | null>(null);

  // Fetch captions when the workspace changes. Guard against a race where
  // workspaceId changes mid-flight: an in-flight Promise.all from the previous
  // workspace must NOT win and repopulate the picker with the wrong workspace's
  // captions.
  useEffect(() => {
    let isActive = true;
    const loadCaptions = async () => {
      try {
        const [userCaptionsData, trebbleCaptionsData] = await Promise.all([
          fetchUserCaptions(workspaceId),
          fetchTrebbleCaptions(workspaceId)
        ]);
        if (!isActive) return;

        // Organize Trebble captions by category
        const categorizedCaptions = trebbleCaptionsData.reduce((acc: { [key: string]: SavedCaptionDesignSystemConfig[] }, caption) => {
          const category = 'Trebble Presets'; // Default category for Trebble captions
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(caption);
          return acc;
        }, {});

        setUserCaptions(userCaptionsData);
        setTrebbleCaptionsByCategory(categorizedCaptions);
      } catch (error) {
        if (!isActive) return;
        console.error("Error loading captions:", error);
        window.alertErrorMessage(error);
      }
    };
    loadCaptions();
    return () => {
      isActive = false;
    };
  }, [workspaceId]);

  // Notify parent about picker visibility
  useEffect(() => {
    const isAnyPickerVisible = isBackgroundPickerVisible || isCaptionPickerVisible;
    onPickerVisibilityChange?.(isAnyPickerVisible);
  }, [isBackgroundPickerVisible, isCaptionPickerVisible, onPickerVisibilityChange]);

  // Reset the background-image cache whenever the workspace changes so the
  // next picker open fetches fresh assets for the new workspace.
  useEffect(() => {
    setBackgroundImages([]);
    setBackgroundImagesWorkspaceId(undefined);
    setBackgroundImageError(null);
  }, [workspaceId]);

  // Tracks the most recently requested workspace so an in-flight fetch can
  // detect that the workspace changed under it and discard its result. Without
  // this, a switch from workspace A → B → A while the A→B fetch is still in
  // flight can briefly show A's images even after the user is back on A.
  const latestRequestedWorkspaceIdRef = useRef<string | null | undefined>(workspaceId);
  useEffect(() => {
    latestRequestedWorkspaceIdRef.current = workspaceId;
  }, [workspaceId]);

  // Fetch background images when the picker becomes visible. Refetch when the
  // cached results are from a different workspace than the current one, OR
  // when no images are cached yet (initial / default-workspace case where
  // backgroundImagesWorkspaceId and workspaceId are both undefined — strict
  // !== would skip the fetch).
  //
  // The `!isLoadingBackgroundImages` guard + dependency closes a race:
  // workspace A → B during an in-flight A fetch lands here while loading is
  // still true. Without the guard the effect would call fetchBackgroundImages
  // which short-circuits on its own isLoading check and never gets re-fired
  // for B (deps haven't changed by the time A's fetch resolves and clears
  // the loading flag). Watching the loading flag here re-fires the effect
  // when A's request completes, at which point we issue the B fetch.
  useEffect(() => {
    if (
      isBackgroundPickerVisible &&
      !isLoadingBackgroundImages &&
      (backgroundImagesWorkspaceId !== workspaceId || backgroundImages.length === 0)
    ) {
      fetchBackgroundImages();
    }
  }, [
    isBackgroundPickerVisible,
    isLoadingBackgroundImages,
    backgroundImagesWorkspaceId,
    backgroundImages.length,
    workspaceId
  ]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onThemeChange({
      ...theme,
      name: e.target.value
    });
  };

  const handleColorChange = (updatedBrandConfig: BrandSystemConfig) => {
    onThemeChange({
      ...theme,
      themeConfig: {
        ...theme.themeConfig,
        brandConfig: updatedBrandConfig
      }
    });
  };

  const handleMediaChange = (media: MediaItem) => {
      onThemeChange({
        ...theme,
        themeConfig: {
          ...theme.themeConfig,
          backgroundConfig: {
            ...theme.themeConfig.backgroundConfig,
            imageURL: media.url,
            format: 'image'
          }
        }
      });
  };


  const handleCaptionSelect = (savedCaption: SavedCaptionDesignSystemConfig) => {
    onThemeChange({
      ...theme,
      themeConfig: {
        ...theme.themeConfig,
        savedCaptionConfig: savedCaption
      }
    });
    setIsCaptionPickerVisible(false);
  };

  // Create a sample caption page for preview
  const previewPage = {
    text: "This is a sample caption preview",
    tokens: [{
      text: "This is a sample caption preview",
      fromMs: 0,
      toMs: 3000
    }],
    startMs: 0
  } as CaptionPage;

  const fetchBackgroundImages = async (): Promise<MediaItem[]> => {
    if (isLoadingBackgroundImages) return backgroundImages;

    // Capture the workspace this request was issued for so we can discard
    // a stale result if the workspace changes while the API call is in
    // flight. Without this, a fast workspace switch can briefly repopulate
    // the picker with the previous workspace's images.
    const requestedWorkspaceId = workspaceId;

    setIsLoadingBackgroundImages(true);
    setBackgroundImageError(null);
    try {
      const assets = await TrebbleClientAPIHelper.getInstance().getAllBackgroundImagesForEditor(requestedWorkspaceId);
      // Stale-result guard — bail out (without overwriting state) if the
      // workspace prop has changed under us. The newer in-flight fetch
      // (or the workspace-change effect that clears the cache) will
      // populate the correct images.
      if (requestedWorkspaceId !== latestRequestedWorkspaceIdRef.current) {
        return [];
      }
      const mediaItems = assets.map((asset: BackgroundImageAsset) => ({
        id: asset.assetId,
        url: asset.url,
        type: 'image' as const,
        title: asset.name,
        category: 'background'
      }));
      setBackgroundImages(mediaItems);
      // Stamp the cache with the workspace it was fetched for so the picker
      // refetches when the user switches workspaces.
      setBackgroundImagesWorkspaceId(requestedWorkspaceId);
      return mediaItems;
    } catch (error) {
      // Don't overwrite UI state for a stale rejection either — same
      // rationale as the success-path guard.
      if (requestedWorkspaceId !== latestRequestedWorkspaceIdRef.current) {
        return [];
      }
      console.error('Failed to fetch background images:', error);
      setBackgroundImageError('Failed to load background images');
      TrebbleMessage.error('Failed to load background images');
      return [];
    } finally {
      setIsLoadingBackgroundImages(false);
    }
  };

  const handleBackgroundImageUpload = async (file: File): Promise<MediaItem> => {
    try {
      const asset = await FileUploadHelper.getInstance().showUploadDialogAndUploadImageAssetToLibrary(
        file,
        file.name,
        "background_image",
        true,
        null,
        { workspaceId }
      );

      return {
        id: asset.assetId,
        url: asset.url,
        type: 'image' as const,
        title: asset.name,
        category: 'background'
      };
    } catch (error) {
      console.error('ThemeEditForm upload failed:', error);
      TrebbleMessage.error('Failed to upload background image');
      throw error;
    }
  };

  return (
    <div className="flex flex-col h-full">
      <TrebbleMessage._configurator />

      {!isBackgroundPickerVisible && !isCaptionPickerVisible && (
        <>
          {/* Basic Settings Card */}
          <TrebbleCard size="small" title={window.getI18n(ti18n, "BASIC_SETTINGS_TITLE")} className="mt-2">
            <FieldContainer 
              label={window.getI18n(ti18n, "THEME_NAME_LABEL")}
              description={window.getI18n(ti18n, "THEME_NAME_DESCRIPTION")}
            >
              <TrebbleInput
                className="w-full"
                value={theme.name}
                onChange={handleNameChange}
                placeholder={window.getI18n(ti18n, "THEME_NAME_PLACEHOLDER")}
              />
            </FieldContainer>
          </TrebbleCard>

          {/* Colors Card */}
          <TrebbleCard size="small" title={window.getI18n(ti18n, "COLORS_TITLE")} className="mt-4">
            <ColorComponent 
              brandConfig={theme.themeConfig.brandConfig}
              onBrandConfigChange={handleColorChange}
            />
          </TrebbleCard>

          {/* Border Settings Card */}
          <TrebbleCard size="small" title={window.getI18n(ti18n, "BORDER_SETTINGS_TITLE")} className="mt-4">
            <FieldContainer 
              label={window.getI18n(ti18n, "BORDER_RADIUS_LABEL")}
              description={window.getI18n(ti18n, "BORDER_RADIUS_DESCRIPTION")}
            >
              <IconDropdown
                className="w-full"
                options={Object.values(BORDER_CORNER_RADIUS)}
                value={Object.values(BORDER_CORNER_RADIUS).find(
                  option => option.value === theme.themeConfig.brandConfig.borderRadius
                )?.value ?? BORDER_CORNER_RADIUS.SMOOTH.value}
                onChange={(value) => onThemeChange({
                  ...theme,
                  themeConfig: {
                    ...theme.themeConfig,
                    brandConfig: {
                      ...theme.themeConfig.brandConfig,
                      borderRadius: parseFloat(value as string)
                    }
                  }
                })}
              />
            </FieldContainer>
          </TrebbleCard>

          {/* Background Settings Card */}
          <TrebbleCard size="small" title={window.getI18n(ti18n, "BACKGROUND_SETTINGS_TITLE")} className="mt-4">
            <FieldContainer label={window.getI18n(ti18n, "BACKGROUND_STYLE_LABEL")}>
              <TrebbleRadio.Group
                block
                optionType="button"
                buttonStyle="solid"
                options={Object.values(BACKGROUND_STYLES)}
                value={theme.themeConfig.backgroundConfig.format}
                onChange={(e: TrebbleRadioChangeEvent) => onThemeChange({
                  ...theme,
                  themeConfig: {
                    ...theme.themeConfig,
                    backgroundConfig: {
                      ...theme.themeConfig.backgroundConfig,
                      format: e.target.value as 'image' | 'color'
                    }
                  }
                })}
              />
            </FieldContainer>

            {theme.themeConfig.backgroundConfig.format === 'color' && (
              <FieldContainer label={window.getI18n(ti18n, "BACKGROUND_COLOR_LABEL")}>
                <TrebbleColorPicker
                  value={theme.themeConfig.backgroundConfig.color}
                  onChange={(color) => onThemeChange({
                    ...theme,
                    themeConfig: {
                      ...theme.themeConfig,
                      backgroundConfig: {
                        ...theme.themeConfig.backgroundConfig,
                        color: color.toHexString()
                      }
                    }
                  })}
                />
              </FieldContainer>
            )}

            {theme.themeConfig.backgroundConfig.format === 'image' && (
              <FieldContainer label={window.getI18n(ti18n, "BACKGROUND_IMAGE_LABEL")}>
                <div style={{ position: 'relative' }}>
                  {theme.themeConfig.backgroundConfig.imageURL ? (
                    <>
                      <div
                        className="border-2 border-gray-200 bg-white flex items-center justify-center aspect-video w-full h-full p-2 box-border dark:bg-neutral-800 dark:border-neutral-600"
                      >
                        <img
                          src={Utils.getInstance().getImageUrlResizeToSmallSize(theme.themeConfig.backgroundConfig.imageURL)}
                          alt="Background"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            display: 'block',
                          }}
                        />
                      </div>
                      <div className="w-full mt-2 flex">
                        <TrebbleButton onClick={() => setIsBackgroundPickerVisible(true)} className="w-full">
                          {window.getI18n(ti18n, "REPLACE_IMAGE_BUTTON")}
                        </TrebbleButton>
                      </div>
                    </>
                  ) : (
                    <div
                      onClick={() => setIsBackgroundPickerVisible(true)}
                      style={{
                        width: '100%',
                        aspectRatio: '16/9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px dashed var(--color-border)',
                        cursor: 'pointer'
                      }}
                    >
                      <TrebbleButton type="primary">{window.getI18n(ti18n, "SELECT_IMAGE_BUTTON")}</TrebbleButton>
                    </div>
                  )}
                </div>
              </FieldContainer>
            )}
          </TrebbleCard>

          {/* Caption Settings Card */}
          <TrebbleCard size="small" title={window.getI18n(ti18n, "CAPTION_SETTINGS_TITLE")} className="mt-4">
            <FieldContainer label={window.getI18n(ti18n, "CAPTION_STYLE_LABEL")}>
              <div style={{ position: 'relative' }}>
                {theme.themeConfig.savedCaptionConfig ? (
                  <>
                    <div className="border p-2 dark:border-neutral-600">
                      <ClassicCaptionPreview
                        page={previewPage}
                        fontFamily={theme.themeConfig.savedCaptionConfig.captionConfig.font}
                        canvas={{
                          width: 400,
                          height: 120,
                          aspectRatio: "16:9",
                          safeZone: {
                            top: 0.1,
                            bottom: 0.1,
                            left: 0.1,
                            right: 0.1
                          }
                        }}
                        maxLines={theme.themeConfig.savedCaptionConfig.captionConfig.maxLines}
                        primaryColor={theme.themeConfig.savedCaptionConfig.captionConfig.useDesignSystemColor?theme.themeConfig.brandConfig.primaryColor: theme.themeConfig.savedCaptionConfig.captionConfig.mainColor}
                        currentSpokenWordColor={theme.themeConfig.savedCaptionConfig.captionConfig.useDesignSystemColor?theme.themeConfig.brandConfig.accentColor: theme.themeConfig.savedCaptionConfig.captionConfig.highlightColor}
                        maxFontSize={Number(theme.themeConfig.savedCaptionConfig.captionConfig.fontSizeRatio) || 48}
                        outline={theme.themeConfig.savedCaptionConfig.captionConfig.outline}
                        background={theme.themeConfig.savedCaptionConfig.captionConfig.background}
                        textAnimation={theme.themeConfig.savedCaptionConfig.captionConfig.textAnimation}
                        borderRadius={theme.themeConfig.savedCaptionConfig.captionConfig.borderRadius}
                        captionCase={theme.themeConfig.savedCaptionConfig.captionConfig.captionCase}
                        hidePunctuation={theme.themeConfig.savedCaptionConfig.captionConfig.hidePunctuation}
                        position={theme.themeConfig.savedCaptionConfig.captionConfig.position as Position}
                      />
                    </div>
                    <div className="w-full mt-2 flex">
                      <TrebbleButton onClick={() => setIsCaptionPickerVisible(true)} className="w-full">
                        {window.getI18n(ti18n, "CHANGE_CAPTION_STYLE_BUTTON")}
                      </TrebbleButton>
                    </div>
                  </>
                ) : (
                  <div
                    onClick={() => setIsCaptionPickerVisible(true)}
                    style={{
                      width: '100%',
                      aspectRatio: '16/9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed var(--color-border)',
                      cursor: 'pointer'
                    }}
                  >
                    <TrebbleButton type="primary">{window.getI18n(ti18n, "SELECT_CAPTION_STYLE_BUTTON")}</TrebbleButton>
                  </div>
                )}
              </div>
            </FieldContainer>
          </TrebbleCard>

          {/* Typography Settings Card */}
          <TrebbleCard size="small" title={window.getI18n(ti18n, "TYPOGRAPHY_SETTINGS_TITLE")} className="mt-4">
            <FieldContainer 
              label={window.getI18n(ti18n, "TITLE_FONT_SETTINGS_LABEL")}
              description={window.getI18n(ti18n, "TITLE_FONT_SETTINGS_DESCRIPTION")}
            >
              <div className="flex flex-col gap-4">
                <div>
                  <div className="mb-2 text-sm ">{window.getI18n(ti18n, "FONT_FAMILY_LABEL")}</div>
                  <FontSelect 
                    className="w-full"
                    value={theme.themeConfig.brandConfig?.titleFont as FontValue}
                    onChange={(newFont: FontValue) => 
                      onThemeChange({
                        ...theme,
                        themeConfig: {
                          ...theme.themeConfig,
                          brandConfig: {
                            ...theme.themeConfig.brandConfig,
                            titleFont: newFont
                          }
                        }
                      })}
                  />
                </div>

                <div>
                  <div className="mb-2 text-sm ">{window.getI18n(ti18n, "OUTLINE_STYLE_LABEL")}</div>
                  <TrebbleSelect
                    className="w-full"
                    options={Object.values(FONT_OUTLINE)}
                    value={theme.themeConfig.brandConfig?.titleFontOutline}
                    onChange={(newOutline) => onThemeChange({
                      ...theme,
                      themeConfig: {
                        ...theme.themeConfig,
                        brandConfig: {
                          ...theme.themeConfig.brandConfig,
                          titleFontOutline: newOutline
                        }
                      }
                    })}
                  />
                </div>
              </div>
            </FieldContainer>

            <FieldContainer 
              label={window.getI18n(ti18n, "BODY_FONT_SETTINGS_LABEL")}
              description={window.getI18n(ti18n, "BODY_FONT_SETTINGS_DESCRIPTION")}
            >
              <div className="flex flex-col gap-4">
                <div>
                  <div className="mb-2 text-sm ">{window.getI18n(ti18n, "FONT_FAMILY_LABEL")}</div>
                  <FontSelect 
                    className="w-full"
                    value={theme.themeConfig.brandConfig?.bodyFont as FontValue}
                    onChange={(newFont: FontValue) => onThemeChange({
                      ...theme,
                      themeConfig: {
                        ...theme.themeConfig,
                        brandConfig: {
                          ...theme.themeConfig.brandConfig,
                          bodyFont: newFont
                        }
                      }
                    })}
                  />
                </div>

                <div>
                  <div className="mb-2 text-sm">{window.getI18n(ti18n, "OUTLINE_STYLE_LABEL")}</div>
                  <TrebbleSelect
                    className="w-full"
                    options={Object.values(FONT_OUTLINE)}
                    value={theme.themeConfig.brandConfig?.bodyFontOutline}
                    onChange={(newOutline) => onThemeChange({
                      ...theme,
                      themeConfig: {
                        ...theme.themeConfig,
                        brandConfig: {
                          ...theme.themeConfig.brandConfig,
                          bodyFontOutline: newOutline
                        }
                      }
                    })}
                  />
                </div>
              </div>
            </FieldContainer>
          </TrebbleCard>
        </>
      )}

      {isCaptionPickerVisible && (
        <TrebbleCard size="small" className="my-2">
            <div className="flex flex-col h-full">
                {/* Header - Fixed at top */}
                <div className="sticky top-0 z-20 bg-white border-b dark:bg-neutral-800 dark:border-neutral-600">
                    <div className="flex items-center p-2">
                        <TrebbleButton 
                            type="text" 
                            icon={<ArrowLeftOutlined />}
                            onClick={() => setIsCaptionPickerVisible(false)}
                        >
                            {window.getI18n(ti18n, "BACK_BUTTON")}
                        </TrebbleButton>
                    </div>
                </div>

                {/* Caption List - Scrollable content */}
                <div className="flex-grow overflow-auto">
                    <CaptionList
                        userCaptions={userCaptions}
                        trebbleCaptionsByCategory={trebbleCaptionsByCategory}
                        selectedCaption={theme.themeConfig.savedCaptionConfig}
                        selectedCaptionDetails={theme.themeConfig.savedCaptionConfig}
                        brandConfig={theme.themeConfig.brandConfig}
                        onCaptionSelect={handleCaptionSelect}
                        onEdit={() => {}}
                        onDuplicate={() => {}}
                        showActions={false}
                    />
                </div>
            </div>
        </TrebbleCard>
      )}

      {isBackgroundPickerVisible && theme.themeConfig.backgroundConfig.format === 'image' && (
        <TrebbleCard size="small" className="my-2">
          <MediaSelector
            className="my-2"
            compact={true}
            selectedMedia={theme.themeConfig.backgroundConfig.imageURL ? {
              id: 'current',
              url: theme.themeConfig.backgroundConfig.imageURL,
              type: 'image',
              title: window.getI18n(ti18n, "CURRENT_BACKGROUND_TITLE"),
              category: 'nature'
            } : undefined}
            onMediaChange={handleMediaChange}
            categories={BACKGROUND_CATEGORIES}
            mediaItems={backgroundImages}
            type="image"
            uploadToBackend={handleBackgroundImageUpload}
            isLoading={isLoadingBackgroundImages}
            error={backgroundImageError || undefined}
            isVisible={isBackgroundPickerVisible}
            onClose={() => setIsBackgroundPickerVisible(false)}
          />
        </TrebbleCard>
      )}
    </div>
  );
};
